import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { PersonalizationUserScope } from '@decisions/server/DecisionsFramework/Design/Page/Personalization/PersonalizationUserScope';
import { PersonalizationScope } from '@decisions/server/DecisionsFramework/Design/Page/Personalization/PersonalizationScope';
import { IconButton } from 'components/buttons/IconButton';
import { Callout } from 'components/layout/Callout';
import { ActionLink } from 'components/ActionLinks/ActionLink';
import { Icon, IconSize } from 'components/icons/Icon';
import './PersonalizationScopeSelector.less';
import { ActionLinkGroupTitle } from 'components/ActionLinks/ActionLinkGroupTitle';

export interface IPersonalizationScopeSelectorProps {
  scope: PersonalizationScope;
  userScope: PersonalizationUserScope;
  onScopeChange: (value: PersonalizationScope) => void;
  onUserScopeChange: (value: PersonalizationUserScope) => void;
  active?: boolean;
  small?: boolean;
  /** true if a setting can't sensibly be applied at a page level */
  partOnly?: boolean;
}

const getClassName = (active?: boolean, small?: boolean) =>
  classNames('dp-p12n-scope-selector', {
    'dp-p12n-scope-selector--inactive': !active,
    'dp-p12n-scope-selector--active': active,
    'dp-p12n-scope-selector--small': small,
  });

const getScopeClassName = (scope?: PersonalizationScope) =>
  classNames({
    'dp-p12n-scope__icon--scope-part': scope === PersonalizationScope.Part,
    'dp-p12n-scope__icon--scope-page': scope === PersonalizationScope.Page,
  });

const getUserScopeClassName = (userScope?: PersonalizationUserScope) =>
  classNames({
    // 'dp-p12n-scope__icon--scope-session': userScope === PersonalizationUserScope.Session, // unused, commented for now
    'dp-p12n-scope__icon--scope-user': userScope === PersonalizationUserScope.User,
    'dp-p12n-scope__icon--scope-all': userScope === PersonalizationUserScope.AllUsers,
  });

const getScopeLabel = (scope?: PersonalizationScope) => {
  return scope === PersonalizationScope.Page ? 'Page' : 'Part';
};

const getUserScopeLabel = (userScope?: PersonalizationUserScope) => {
  switch (userScope) {
    case PersonalizationUserScope.AllUsers:
      return 'All Users';
    // case PersonalizationUserScope.User:
    default:
      // make User the default for now:
      return 'User';
    // default:
    //   return 'Session';
  }
};

/**
 * Choose scope for a personalization value.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function PersonalizationScopeSelector({
  active,
  onScopeChange,
  onUserScopeChange,
  partOnly,
  scope,
  small,
  userScope,
}: IPersonalizationScopeSelectorProps) {
  const [open, setOpen] = useState<'scope' | 'userScope' | ''>('');
  const scopeRef = useRef(null);
  const userScopeRef = useRef(null);

  const onScopeSelect = (value: PersonalizationScope) => {
    onScopeChange(value);
    setOpen('');
  };

  const onUserScopeSelect = (value: PersonalizationUserScope) => {
    setOpen('');
    onUserScopeChange(value);
  };

  const noop = () => {};

  const iconSize: IconSize = small ? 'small' : 'native';

  return (
    <div className={getClassName(active, small)}>
      {!partOnly && (
        <IconButton
          ref={scopeRef}
          className="dp-p12n-scope__button"
          iconClassName={getScopeClassName(scope)}
          onClick={() => setOpen('scope')}
          title={getScopeLabel(scope)}
          iconSize={iconSize}
        />
      )}
      <IconButton
        ref={userScopeRef}
        className="dp-p12n-scope__button"
        iconClassName={getUserScopeClassName(userScope)}
        onClick={() => setOpen('userScope')}
        title={getUserScopeLabel(userScope)}
        iconSize={iconSize}
      />
      <Callout
        isOpen={!!open}
        onClose={() => setOpen('')}
        sourceRef={open === 'scope' ? scopeRef : userScopeRef}
        placement="bottom"
        showArrow={true}
        flipBehavior="counterclockwise"
      >
        {open === 'scope' && (
          <>
            <ActionLinkGroupTitle>Apply To</ActionLinkGroupTitle>
            <ActionLink
              value={PersonalizationScope.Part}
              label={getScopeLabel(PersonalizationScope.Part)}
              onClick={onScopeSelect}
              onMouseOver={noop}
              isActive={scope === PersonalizationScope.Part}
              beforeLabel={<Icon className="dp-p12n-scope__link-icon dp-p12n-scope__icon--scope-part" size="native" />}
            />
            <ActionLink
              value={PersonalizationScope.Page}
              label={getScopeLabel(PersonalizationScope.Page)}
              onClick={onScopeSelect}
              onMouseOver={noop}
              isActive={scope === PersonalizationScope.Page}
              beforeLabel={<Icon className="dp-p12n-scope__link-icon dp-p12n-scope__icon--scope-page" size="native" />}
            />
          </>
        )}
        {open === 'userScope' && (
          <>
            <ActionLinkGroupTitle>Apply For</ActionLinkGroupTitle>
            {/* 
            <ActionLink
              value={PersonalizationUserScope.Session}
              label={getUserScopeLabel(PersonalizationUserScope.Session)}
              callback={onUserScopeSelect}
              onMouseOver={noop}
              isActive={userScope === PersonalizationUserScope.Session}
              beforeLabel={
                <Icon className="dp-p12n-scope__link-icon dp-p12n-scope__icon--scope-session" size="native" />
              }
            />
            */}
            <ActionLink
              value={PersonalizationUserScope.User}
              label={getUserScopeLabel(PersonalizationUserScope.User)}
              onClick={onUserScopeSelect}
              onMouseOver={noop}
              isActive={userScope === PersonalizationUserScope.User}
              beforeLabel={<Icon className="dp-p12n-scope__link-icon dp-p12n-scope__icon--scope-user" size="native" />}
            />
            <ActionLink
              value={PersonalizationUserScope.AllUsers}
              label={getUserScopeLabel(PersonalizationUserScope.AllUsers)}
              onClick={onUserScopeSelect}
              onMouseOver={noop}
              isActive={userScope === PersonalizationUserScope.AllUsers}
              beforeLabel={<Icon className="dp-p12n-scope__link-icon dp-p12n-scope__icon--scope-all" size="native" />}
            />
          </>
        )}
      </Callout>
    </div>
  );
}
